import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

export function ConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasConsented = localStorage.getItem('cookieConsentGiven');
    if (!hasConsented) {
      // Small delay for natural appearance
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = async () => {
    localStorage.setItem('cookieConsentGiven', 'true');
    setIsVisible(false);
    
    // If logged in, update consent on backend
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        await api.post('/api/privacy/consent', {
          consentGiven: true,
          consentVersion: 'v1.0'
        });
      } catch (err) {
        console.error('Failed to sync consent status with backend:', err);
      }
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ duration: 0.4 }}
        className="fixed bottom-6 inset-x-6 z-50 max-w-4xl mx-auto"
        role="dialog"
        aria-label="Cookie and data consent"
      >
        <div className="bg-white/95 dark:bg-[#0D1F3C]/95 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl p-6 shadow-[0_10px_50px_rgba(0,0,0,0.3)] flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-4 text-left">
            <div className="p-3 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 rounded-xl flex-shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              {/* Not a real heading -- this is a transient overlay dialog, not
                  part of the document's content hierarchy, so an <h4> here
                  would skip levels depending on whatever page it floats over. */}
              <p className="font-extrabold text-slate-900 dark:text-white text-sm">GDPR & DPDP Act Data Consent</p>
              <p className="text-xs text-slate-600 dark:text-white/60 mt-1 leading-relaxed">
                We use cookies and business metrics to personalize your matching government schemes, business health calculations, and insurance quotes. By clicking "Accept", you agree to our processing of analytics data. Read our{' '}
                <Link to="/privacy-policy" className="text-cyan-600 dark:text-cyan-400 font-bold hover:underline">
                  Privacy Policy
                </Link>{' '}
                for details.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto">
            <button
              onClick={() => setIsVisible(false)}
              className="flex-1 md:flex-none px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-center transition"
            >
              Configure
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 md:flex-none px-6 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md hover:from-blue-500 hover:to-cyan-400 text-center transition"
            >
              Accept All
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
